<script lang="ts" context="module">
  export const changelogModal = new ModalQueryState('changelog');
</script>

<script lang="ts">
  import Modal from '../components/Modal.svelte';
  import ActionButton from '../components/ActionButton.svelte';
  import MailIcon from '~icons/feather/mail';
  import Alphabetical from '~icons/mdi/alphabetical';
  import BookOpen from '~icons/mdi/book-open-variant';
  import Clock from '~icons/mdi/clock-outline';
  import Music from '~icons/mdi/music';
  import Close from '~icons/mdi/close';
  import Delta from '~icons/mdi/delta';
  import Youtube from '~icons/mdi/youtube';
  import { onMount, tick } from 'svelte';
  import Feedback from './Feedback.svelte';
  import DiscordButton from '../components/buttons/DiscordButton.svelte';
  import TikTok from '../icons/TikTok.svelte';
  import Premium from '~/icons/Premium.svelte';
  import { ModalQueryState } from '~/lib/nav.svelte';
  import { premiumModal } from '~/modals/PremiumModal.svelte';
  import {
    CHANGELOG_VERSION,
    changelogVersion,
    changelogViewed,
  } from '~/tutorial/state.svelte';
  import Discord from '~/icons/Discord.svelte';
  import Spotify from '~/icons/Spotify.svelte';
  import type { ModalSection } from '~/types';
  import Directory from '~/components/Directory.svelte';

  const sections: ModalSection[] = [
    {
      id: 'premium-changelog',
      icon: Premium,
      desc: 'Gram Jam Premium',
    },
    {
      id: 'discord-changelog',
      icon: Discord,
      desc: 'Join the Discord',
    },
    {
      id: 'soundtrack-changelog',
      icon: Spotify,
      desc: 'Soundtrack',
    },
    {
      id: 'contact-changelog',
      icon: MailIcon,
      desc: 'Contact',
    },
  ];

  onMount(async () => {
    // don't show changelog to first-time players
    if (!changelogVersion.current) {
      changelogVersion.current = CHANGELOG_VERSION;
    }
    if (!changelogViewed.current) {
      await tick();
      await handleOpen();
    }
  });

  export const handleOpen = async () => {
    await changelogModal.push();
  };

  const handleClose = async () => {
    changelogVersion.current = CHANGELOG_VERSION;
    await changelogModal.pop();
  };
</script>

<Modal index={2} open={changelogModal.open} onClose={handleClose}>
  {#snippet title()}
    <Delta color="#4CAF50" />
    Updates
  {/snippet}
  {#snippet content()}
    <div class="content">
      <p>
        A new update is here! Here's what's new:
      </p>

      <h2><BookOpen /> Better Definitions</h2>
      <p>
        Word definitions now come from <b>Wiktionary</b>, so more words have
        definitions and they load more reliably. Definitions also show how the
        word is pronounced, and many words have a play button so you can hear
        it spoken. Tap any word in your word history to look it up.
      </p>

      <h2><Clock /> Word Timestamps</h2>
      <p>
        Your word history now shows how long ago you found each word. This is toggleable in settings.
      </p>

      <h2>💥 What Else Is New</h2>
      <ul class="feature-list">
        <li>
          <b>Smoother animations</b> - Dragging tiles and clearing words have
          been reworked to feel smoother, especially on mobile. Tiles don't get 
          stuck anymore if you drag too quickly.
        </li>
      </ul>

      <h2>🐛 Bug Fixes</h2>
      <ul class="feature-list">
        <li>Fixed tiles sometimes getting stuck after quick swaps.</li>
        <li>Fixed the word history sometimes showing up empty or out of date after sorting.</li>
        <li>Fixed full clear stars missing from the calendar.</li>
        <li>Fixed returning players sometimes not seeing the daily timer or the new puzzle.</li>
        <li>Fixed the replay button asking for premium and an error when signing in with a linked account.</li>
      </ul>

      <h2><Alphabetical /> Previously: Alphabet Bar</h2>
      <p>
        In case you missed it, classic mode has an <b>alphabet bar</b> you can
        slide beneath the board to light up matching tiles on the board. Turn it on
        with the <b>Alphabet bar</b> option in the settings menu.
      </p>

      <p>
        Join our growing Discord community! We love to chat about strategies,
        high scores, and ideas for improving the game. If you think the
        dictionary is missing a word, let us know!
        <span class="links" style:margin-top="1em">
          <DiscordButton />
        </span>
      </p>

      <div class="header" id="contact-changelog">
        <MailIcon />
        <h2>Contact and Support</h2>
      </div>
      <p>
        You can submit in-app feedback anonymously via the contact form (<b>
          I cannot respond directly if you are anonymous</b
        >). There's also a support email
        <a href="mailto:support@gramjam.app?subject=Gram Jam Feedback">
          support@gramjam.app
        </a>
        if you want to hear back from me. I always love hearing from fans and getting
        suggestions, and will reply to all emails I receive.
        <br />
        <br />
        <Feedback />
      </p>

      <h2>Thank You!</h2>
      <p>
        And a big thanks to the creators that gave Gram Jam a spotlight on their
        channels. Check them out for more word game content!
      </p>
      <ul>
        <li>
          <a href="https://www.youtube.com/c/TomSolves">
            <Youtube size="1.2em" />Solver Tom
          </a>
        </li>
        <li>
          <a href="https://www.youtube.com/@Rangsk">
            <Youtube size="1.2em" />Rangsk
          </a>
        </li>
        <li>
          <a href="https://www.tiktok.com/@michael.dicostanzo">
            <TikTok size="1em" />
            Michael DiCostanzo
          </a>
        </li>
        <li>
          <a href="https://www.tiktok.com/@wordletips">
            <TikTok size="1em" />Wordletips
          </a>
        </li>
      </ul>
    </div>
  {/snippet}
  {#snippet controls()}
    <ActionButton onClick={handleClose}>
      <Close />
      Close
    </ActionButton>
  {/snippet}
</Modal>

<style lang="scss">
  @use '~/theme/include-media' as *;
  @use '~/theme/themify' as *;
  h2 {
    width: 100%;
    text-align: center;
  }
  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  .header {
    margin-top: 3em;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1em;
    :global(svg) {
      width: 2.5rem;
      height: 2.5rem;
    }
  }

  img {
    border: 2px solid currentColor;
    border-radius: 1em;
    margin-bottom: 1em;
  }

  .feature-list {
    text-align: left;
    padding-left: 1.5em;
    width: 100%;
    li {
      margin-bottom: 0.5em;
    }
  }

  li > a {
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: start;
    gap: 0.5em;
    font-size: 1.25rem;
    color: black;
    width: 100%;
    @include media('<=tablet') {
      font-size: 1.25rem;
    }
  }
  a {
    @include themify {
      color: themed('link');
    }
  }

  .content :global(.action) {
    padding: 0.5em;
    margin-left: auto;
    margin-right: auto;
  }

  .links {
    display: grid;
    align-items: center;
    justify-items: center;
    gap: 0.5em;
  }

  .donate {
    padding-top: 1em;
    animation: shake 2s infinite;
  }

  @keyframes shake {
    50%,
    100% {
      transform: rotate(0);
    }
    5%,
    45% {
      transform: rotate(-2deg);
    }

    10%,
    40% {
      transform: rotate(2deg) scale(1.05);
    }

    15%,
    25%,
    35% {
      transform: rotate(-4deg) scale(1.1);
    }

    20%,
    30% {
      transform: rotate(4deg) scale(1.1);
    }
  }
</style>
